import React, { useState, useEffect } from 'react';
import { 
  CheckCircleIcon, 
  XCircleIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useQNetData } from '../contexts/QNetDataContext';

/**
 * 자격요건 확인 컴포넌트
 * Q-Net API에서 자격요건 정보를 가져와 체크리스트 형태로 표시합니다.
 */
const EligibilityChecker = ({ examCode, className = '' }) => {
  const [eligibilityData, setEligibilityData] = useState(null);
  const [checkedRequirements, setCheckedRequirements] = useState({});
  const [isEligible, setIsEligible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!examCode) return;

    const loadBatchEligibilityData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // 배치 시스템에서 생성된 응시자격 데이터 로드
        const response = await fetch('/qnet-eligibility.json');
        if (!response.ok) {
          throw new Error('배치 응시자격 데이터 로드 실패');
        }
        
        const batchData = await response.json();
        const examEligibilityData = batchData.eligibility[examCode] || batchData.eligibility[examCode.toString()];
        
        if (examEligibilityData) {
          // 배치 데이터를 컴포넌트에서 사용할 수 있는 형태로 변환
          const transformedData = {
            title: examEligibilityData.examName || examCode,
            description: `${examEligibilityData.level} 응시자격 요건`,
            requirements: examEligibilityData.requirements || [],
            ageLimit: examEligibilityData.ageLimit || '만 18세 이상',
            additionalNotes: examEligibilityData.additionalNotes
          };
          
          setEligibilityData(transformedData);
        } else {
          // 배치 데이터에 해당 자격증이 없는 경우 mock 데이터 사용
          const mockData = getMockEligibilityData(examCode);
          setEligibilityData(mockData);
        }
        
      } catch (err) {
        console.error('배치 응시자격 데이터 로드 실패:', err);
        
        // 폴백으로 mock 데이터 사용
        try {
          const mockData = getMockEligibilityData(examCode);
          setEligibilityData(mockData);
        } catch (mockError) {
          setError('응시자격 데이터를 불러올 수 없습니다.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadBatchEligibilityData();
  }, [examCode]);

  useEffect(() => {
    checkEligibility();
  }, [checkedRequirements, eligibilityData]);

  // API 응답에서 자격요건 데이터 파싱
  const parseEligibilityData = (requirements) => {
    // 실제 API 응답 구조에 맞게 파싱
    // 현재는 mock 데이터를 반환
    return getMockEligibilityData(examCode);
  };

  // 개발용 mock 데이터 (실제 API 데이터로 대체 예정)
  const getMockEligibilityData = (code) => {
    const mockData = {
      '1150': { // 전기기사
        title: '전기기사',
        description: '전기설비의 설계, 시공, 검사, 운전, 보수 등의 업무를 수행하는 기술인력 양성을 목표로 합니다.',
        categories: [
          {
            type: 'education',
            title: '학력 요건',
            icon: AcademicCapIcon,
            requirements: [
              { id: 'edu_1', text: '대학졸업(4년) 또는 이와 동등 이상의 학력', required: false },
              { id: 'edu_2', text: '대학졸업예정자(4년)', required: false },
              { id: 'edu_3', text: '3년제 전문대학 졸업 + 관련업무 1년 이상 경력', required: false },
              { id: 'edu_4', text: '2년제 전문대학 졸업 + 관련업무 2년 이상 경력', required: false },
              { id: 'edu_5', text: '기능사 + 관련업무 3년 이상 경력', required: false }
            ]
          },
          {
            type: 'experience',
            title: '경력 요건',
            icon: BriefcaseIcon,
            requirements: [
              { id: 'exp_1', text: '관련업무 4년 이상 경력 (학력 무관)', required: false },
              { id: 'exp_2', text: '동일직무분야 산업기사 + 관련업무 1년 이상', required: false }
            ]
          },
          {
            type: 'license',
            title: '자격 요건',
            icon: DocumentTextIcon,
            requirements: [
              { id: 'lic_1', text: '전기기능사 자격증 보유 + 관련업무 3년 이상', required: false },
              { id: 'lic_2', text: '전기산업기사 자격증 보유 + 관련업무 1년 이상', required: false }
            ]
          }
        ]
      },
      '1320': { // 정보처리기사
        title: '정보처리기사',
        description: '정보시스템의 분석, 설계, 구현, 시험, 운영, 유지보수 등의 업무를 수행하는 정보처리 전문인력을 양성합니다.',
        categories: [
          {
            type: 'education',
            title: '학력 요건',
            icon: AcademicCapIcon,
            requirements: [
              { id: 'edu_1', text: '대학졸업(4년) 또는 이와 동등 이상의 학력', required: false },
              { id: 'edu_2', text: '대학졸업예정자(4년)', required: false },
              { id: 'edu_3', text: '3년제 전문대학 졸업 + 관련업무 1년 이상 경력', required: false },
              { id: 'edu_4', text: '2년제 전문대학 졸업 + 관련업무 2년 이상 경력', required: false }
            ]
          },
          {
            type: 'experience',
            title: '경력 요건',
            icon: BriefcaseIcon,
            requirements: [
              { id: 'exp_1', text: '관련업무 4년 이상 경력 (학력 무관)', required: false },
              { id: 'exp_2', text: '정보처리산업기사 + 관련업무 1년 이상', required: false }
            ]
          },
          {
            type: 'license',
            title: '자격 요건',
            icon: DocumentTextIcon,
            requirements: [
              { id: 'lic_1', text: '정보처리기능사 자격증 보유 + 관련업무 3년 이상', required: false },
              { id: 'lic_2', text: '정보처리산업기사 자격증 보유 + 관련업무 1년 이상', required: false }
            ]
          }
        ]
      }
    };

    return mockData[code] || {
      title: '일반 자격증',
      description: '자격요건 정보를 확인하세요.',
      categories: [
        {
          type: 'education',
          title: '학력 요건',
          icon: AcademicCapIcon,
          requirements: [
            { id: 'edu_1', text: '대학졸업(4년) 또는 이와 동등 이상의 학력', required: false }
          ]
        }
      ]
    };
  };

  const handleRequirementChange = (requirementId, checked) => {
    setCheckedRequirements(prev => ({
      ...prev,
      [requirementId]: checked
    }));
  };

  const checkEligibility = () => {
    if (!eligibilityData) return;

    // 각 카테고리별로 최소 하나의 요건이 충족되어야 함
    const categoryResults = eligibilityData.categories.map(category => {
      return category.requirements.some(req => checkedRequirements[req.id]);
    });

    // 모든 카테고리에서 최소 하나씩 충족되거나, 하나의 카테고리에서 충족되면 응시 가능
    const eligible = categoryResults.some(result => result);
    setIsEligible(eligible);
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-lg border border-orange-100 ${className}`}>
        <div className="flex items-center mb-4">
          <ClipboardDocumentCheckIcon className="w-6 h-6 text-orange-500 mr-3" />
          <h3 className="text-xl font-bold text-gray-900">응시자격 확인</h3>
        </div>
        
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-500">자격요건을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-lg border border-orange-100 ${className}`}>
        <div className="flex items-center mb-4">
          <ClipboardDocumentCheckIcon className="w-6 h-6 text-orange-500 mr-3" />
          <h3 className="text-xl font-bold text-gray-900">응시자격 확인</h3>
        </div>
        
        <div className="text-center py-8">
          <ExclamationTriangleIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">{error}</p>
          <p className="text-sm text-gray-400 mb-3">
            Q-Net API에서 자격요건을 가져올 수 없습니다.
          </p>
          <a
            href="https://www.q-net.or.kr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-orange-600 hover:text-orange-700 text-sm font-medium transition-colors"
          >
            Q-net 공식 홈페이지에서 확인하기 →
          </a>
        </div>
      </div>
    );
  }

  if (!eligibilityData) {
    return null;
  }

  const checkedCount = Object.values(checkedRequirements).filter(Boolean).length;

  return (
    <div className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <ClipboardDocumentCheckIcon className="w-6 h-6 text-orange-500 mr-3" />
          <h3 className="text-xl font-bold text-gray-900">응시자격 확인</h3>
        </div>
        
        <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          isEligible 
            ? 'bg-green-100 text-green-800 border border-green-200'
            : 'bg-gray-100 text-gray-600 border border-gray-200'
        }`}>
          {isEligible ? (
            <>
              <CheckCircleIcon className="w-4 h-4 mr-1" />
              응시 가능
            </>
          ) : (
            <>
              <XCircleIcon className="w-4 h-4 mr-1" />
              자격 확인 필요
            </>
          )}
        </div>
      </div>

      {/* 자격증 정보 */}
      <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-6 mb-6">
        <div className="text-lg font-bold text-gray-900 mb-2">
          {eligibilityData.title}
        </div>
        <div className="text-sm text-gray-700">
          {eligibilityData.description}
        </div>
      </div>

      {/* 자격요건 카테고리별 체크리스트 */}
      <div className="space-y-6 mb-6">
        {eligibilityData.categories.map((category, categoryIndex) => {
          const IconComponent = category.icon;
          const categoryCheckedCount = category.requirements.filter(
            req => checkedRequirements[req.id]
          ).length;

          return (
            <div key={categoryIndex} className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <IconComponent className="w-5 h-5 text-orange-500 mr-2" />
                  <h4 className="font-semibold text-gray-900">{category.title}</h4>
                </div>
                <div className="text-sm text-gray-500">
                  {categoryCheckedCount}/{category.requirements.length}
                </div>
              </div>

              <div className="space-y-3">
                {category.requirements.map((requirement, reqIndex) => (
                  <label
                    key={requirement.id}
                    className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      checked={checkedRequirements[requirement.id] || false}
                      onChange={(e) => handleRequirementChange(requirement.id, e.target.checked)}
                    />
                    <div className="flex-1">
                      <div className={`text-sm ${
                        checkedRequirements[requirement.id] 
                          ? 'text-gray-900 font-medium' 
                          : 'text-gray-700'
                      }`}>
                        {requirement.text}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* 결과 안내 */}
      <div className={`border rounded-lg p-4 mb-4 ${
        isEligible 
          ? 'bg-green-50 border-green-200'
          : checkedCount > 0
          ? 'bg-yellow-50 border-yellow-200'
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-start">
          <InformationCircleIcon className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${
            isEligible ? 'text-green-600' : checkedCount > 0 ? 'text-yellow-600' : 'text-gray-500'
          }`} />
          <div>
            <div className={`font-semibold mb-2 ${
              isEligible ? 'text-green-800' : checkedCount > 0 ? 'text-yellow-800' : 'text-gray-700'
            }`}>
              {isEligible 
                ? '축하합니다! 응시자격을 갖추었습니다.'
                : checkedCount > 0
                ? '일부 요건을 충족했습니다.'
                : '응시자격을 확인해주세요.'
              }
            </div>
            <div className={`text-sm ${
              isEligible ? 'text-green-700' : checkedCount > 0 ? 'text-yellow-700' : 'text-gray-600'
            }`}>
              {isEligible
                ? 'Q-Net에서 원서접수를 진행하실 수 있습니다.'
                : checkedCount > 0
                ? '추가 요건을 확인하여 응시자격을 완성해보세요.'
                : '위 항목들을 확인하여 본인의 응시자격을 점검해보세요.'
              }
            </div>
          </div>
        </div>
      </div>

      {/* 추가 안내사항 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <InformationCircleIcon className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium text-blue-800 mb-1">참고사항</div>
            <div className="text-sm text-blue-700">
              • 관련학과는 전공과 유사한 학과를 의미합니다<br />
              • 경력은 해당 분야의 실무경험을 의미합니다<br />
              • 정확한 자격요건은 Q-Net 공식 홈페이지에서 확인하시기 바랍니다
            </div>
          </div>
        </div>
      </div>

      {/* 액션 버튼 */}
      {isEligible && (
        <div className="flex space-x-3">
          <a
            href="https://www.q-net.or.kr"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white text-center py-3 rounded-lg font-medium transition-colors duration-200"
          >
            Q-Net 원서접수 바로가기
          </a>
        </div>
      )}

      {/* 데이터 소스 정보 */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>출처: Q-Net 자격요건</span>
          <span>업데이트: {new Date().toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
};

export default EligibilityChecker;